import React, { useEffect, useState } from "react";
import {
  AlertCircle,
  BarChart2,
  Briefcase,
  Calendar,
  CircleDollarSign,
  Eye,
  Globe,
  Heart,
  Hourglass,
  Lock,
  MessageSquare,
  PauseCircle,
  ShieldCheck,
  Ticket,
  UserCircle,
  Users,
  Wallet,
} from "lucide-react";
import { TeamLogo } from "../bondhouse/FixtureStatus";
import {
  fmtCash,
  fmtCompact,
  fmtFull,
  getFixtureScore,
  getNextKickoff,
} from "../../utils/betDetailHelpers";
import { getFixtureMarketCount } from "../../utils/bondhouseHelpers";
import { formatAppDateTime, formatAppTime } from "../../utils/appTimeFormat";

const PanelHead = ({ tag, title, subtitle, Icon }) => (
  <div className="bd-panel-head">
    <div className="bd-panel-head-copy">
      {tag ? <p className="bd-panel-tag">{tag}</p> : null}
      <h2 className="bd-panel-title">{title}</h2>
      {subtitle ? <p className="bd-panel-sub">{subtitle}</p> : null}
    </div>
    {Icon ? (
      <span className="bd-panel-head-icon">
        <Icon size={16} strokeWidth={2.2} />
      </span>
    ) : null}
  </div>
);

/* ── Header stats (mobile gradient header) ── */

export const DetailHeaderStats = ({
  stats,
  followersCount,
  isFollowing,
  onFollowClick,
  followDisabled,
}) => (
  <section className="bd-header-stats" aria-label="House stats">
    <div className="bd-header-stats-inner">
      <div className="bd-header-stats-grid">
        <div className="bd-header-stat">
          <Wallet size={16} aria-hidden />
          <span className="bd-header-stat-value tabular-nums">{fmtCash(stats.pool)}</span>
          <span className="bd-header-stat-label">Pool</span>
        </div>
        <div className="bd-header-stat">
          <Hourglass size={16} aria-hidden />
          <span className="bd-header-stat-value tabular-nums">{fmtCash(stats.pending)}</span>
          <span className="bd-header-stat-label">Pending</span>
        </div>
        <div className="bd-header-stat">
          <Users size={16} aria-hidden />
          <span className="bd-header-stat-value tabular-nums">{fmtFull(stats.players)}</span>
          <span className="bd-header-stat-label">Players</span>
        </div>
        <div className="bd-header-stat">
          <Heart size={16} aria-hidden />
          <span className="bd-header-stat-value tabular-nums">{fmtFull(followersCount)}</span>
          <span className="bd-header-stat-label">Followers</span>
        </div>
      </div>

      <button
        type="button"
        className={`bd-follow-btn ${isFollowing ? "bd-follow-btn-active" : ""}`}
        onClick={onFollowClick}
        disabled={followDisabled}
        aria-pressed={isFollowing}
      >
        <Heart size={14} strokeWidth={2.4} fill={isFollowing ? "currentColor" : "none"} />
        <span>{isFollowing ? "Following" : "Follow"}</span>
      </button>
    </div>
  </section>
);

/* ── Board snapshot strip ── */

export const BoardSnapshotStrip = ({ board, viewsCount }) => (
  <section className="bd-board-snap" aria-label="Board snapshot">
    <span className="bd-board-snap-pill">
      <Calendar size={12} />
      {board.total} fixture{board.total === 1 ? "" : "s"}
    </span>
    {board.live > 0 ? (
      <span className="bd-board-snap-pill bd-board-snap-pill-live">
        {board.live} live
      </span>
    ) : null}
    {board.upcoming > 0 ? (
      <span className="bd-board-snap-pill">{board.upcoming} upcoming</span>
    ) : null}
    {board.done > 0 ? (
      <span className="bd-board-snap-pill">{board.done} settled</span>
    ) : null}
    {board.suspended > 0 ? (
      <span className="bd-board-snap-pill bd-board-snap-pill-warn">
        {board.suspended} suspended
      </span>
    ) : null}
    <span className="bd-board-snap-pill">
      <BarChart2 size={12} />
      {fmtFull(board.markets)} mkts
    </span>
    <span className="bd-board-snap-pill">
      <Users size={12} />
      {fmtFull(board.boardBettors)} board bettors
    </span>
    <span className="bd-board-snap-pill">
      <Eye size={12} />
      {fmtFull(viewsCount)} views
    </span>
  </section>
);

/* ── Treasury breakdown ── */

export const TreasurySection = ({ treasury }) => {
  const rows = [
    {
      key: "available",
      label: "Available",
      value: treasury.available,
      hint: treasury.availableUpdated,
      pct: treasury.total > 0 ? (treasury.available / treasury.total) * 100 : 0,
      tone: "primary",
    },
    {
      key: "pending",
      label: "Pending (in play)",
      value: treasury.pending,
      hint: treasury.pendingUpdated,
      pct: treasury.total > 0 ? (treasury.pending / treasury.total) * 100 : 0,
      tone: "warn",
    },
    {
      key: "profit",
      label: "House profit held",
      value: treasury.profitHeld,
      hint: treasury.profitUpdated,
      pct: treasury.total > 0 ? (treasury.profitHeld / treasury.total) * 100 : 0,
      tone: "success",
    },
  ].filter((r) => r.value > 0 || r.key === "available");
  const availablePct = rows.find((r) => r.key === "available")?.pct || 0;
  const pendingPct = rows.find((r) => r.key === "pending")?.pct || 0;
  const profitPct = rows.find((r) => r.key === "profit")?.pct || 0;
  const pendingStart = availablePct;
  const pendingEnd = availablePct + pendingPct;
  const profitEnd = Math.min(100, pendingEnd + profitPct);
  const pieStyle = treasury.total > 0
    ? {
        background: `conic-gradient(
          var(--bd-primary) 0% ${availablePct}%,
          var(--bd-secondary) ${pendingStart}% ${pendingEnd}%,
          var(--bd-primary-soft) ${pendingEnd}% ${profitEnd}%,
          rgba(30, 136, 229, 0.14) ${profitEnd}% 100%
        )`,
      }
    : undefined;

  return (
    <section className="bd-panel">
      <PanelHead
        tag="Treasury"
        title="Pool breakdown"
        subtitle="Where the house cash sits right now"
        Icon={CircleDollarSign}
      />

      <div className="bd-treasury-pie-card">
        <div
          className="bd-treasury-pie"
          style={pieStyle}
          role="img"
          aria-label={`Pool allocation: ${Math.round(availablePct)}% available, ${Math.round(pendingPct)}% pending`}
        >
          <div className="bd-treasury-pie-core">
            <span className="bd-treasury-hero-label">Total</span>
            <span className="bd-treasury-hero-value tabular-nums">
              {fmtCash(treasury.balance || treasury.total)}
            </span>
          </div>
        </div>

        <div className="bd-treasury-pie-copy">
          <span className="bd-treasury-pie-kicker">Live allocation</span>
          <strong className="tabular-nums">{Math.round(availablePct)}% available</strong>
          <p>
            Pending cash is isolated from the withdrawable pool until tickets settle.
          </p>
        </div>
      </div>

      <ul className="bd-treasury-rows">
        {rows.map((row) => (
          <li key={row.key} className={`bd-treasury-row bd-treasury-row-${row.tone}`}>
            <div className="bd-treasury-row-head">
              <span className="bd-treasury-row-label">
                <span className="bd-treasury-dot" aria-hidden />
                {row.label}
              </span>
              <span className="bd-treasury-row-value tabular-nums">{fmtCash(row.value)}</span>
            </div>
            <span className="bd-treasury-row-share tabular-nums">
              {Math.round(row.pct)}% of pool
            </span>
            {row.hint ? <span className="bd-treasury-row-hint">Updated {row.hint}</span> : null}
          </li>
        ))}
      </ul>
    </section>
  );
};

/* ── Operators ── */

export const OperatorsSection = ({ operators }) => {
  if (!operators.length) return null;

  return (
    <section className="bd-panel bd-operators-panel">
      <PanelHead
        tag="Team"
        title="House operators"
        subtitle="People running this board"
        Icon={UserCircle}
      />
      <div className="bd-operators-deck">
        {operators.map((op) => (
          <div key={op.id} className="bd-operator-chip">
            <span className="bd-operator-avatar">{(op.name[0] || "?").toUpperCase()}</span>
            <span className="bd-operator-name truncate">{op.name}</span>
          </div>
        ))}
      </div>
    </section>
  );
};

/* ── Recent stakes tape ── */

export const RecentStakesSection = ({ stakes }) => {
  if (!stakes.length) return null;

  return (
    <section className="bd-panel">
      <PanelHead
        tag="Tape"
        title="Recent stakes"
        subtitle="Latest tickets placed on this house"
        Icon={Ticket}
      />
      <ul className="bd-stakes-tape">
        {stakes.map((stake) => (
          <li key={stake.key} className="bd-stakes-tape-row">
            <span className="bd-stakes-tape-label">{stake.label}</span>
            <span className="bd-stakes-tape-amt tabular-nums">{fmtCash(stake.amount)}</span>
            <span className="bd-stakes-tape-when">{stake.when || "—"}</span>
          </li>
        ))}
      </ul>
    </section>
  );
};


export const KickoffCountdown = ({ fixtures }) => {
  const [, tick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => tick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const kickoffData = getNextKickoff(fixtures);
  if (!kickoffData) return null;

  const { fixture, parts } = kickoffData;
  const homeTeam = fixture?.teams?.home;
  const awayTeam = fixture?.teams?.away;
  const home = homeTeam?.name?.trim() || "Home";
  const away = awayTeam?.name?.trim() || "Away";
  const { showScore, home: homeGoals, away: awayGoals, short } = getFixtureScore(fixture);
  const kickoffTime =
    fixture?.date && !Number.isNaN(new Date(fixture.date).getTime())
      ? formatAppTime(fixture.date)
      : "TBD";

  const pad = (n) => String(Math.max(0, n)).padStart(2, "0");
  const segments = [];
  if (parts.days > 0) segments.push(String(parts.days));
  segments.push(pad(parts.hours), pad(parts.minutes), pad(parts.seconds));

  return (
    <section className="bd-kickoff" aria-label="Next kickoff">
      <p className="bd-kickoff-eyebrow">Next kickoff</p>
      <div className="bd-kickoff-clock" aria-live="polite">
        {segments.map((value, index) => (
          <React.Fragment key={`${value}-${index}`}>
            {index > 0 ? <span className="bd-kickoff-colon" aria-hidden>:</span> : null}
            <span className="bd-kickoff-digit tabular-nums">{value}</span>
          </React.Fragment>
        ))}
      </div>
      <div className="bd-kickoff-fixture">
        <div className="bd-kickoff-fixture-main">
          <div className="bd-kickoff-team">
            <TeamLogo team={homeTeam} size="sm" />
            <span className="truncate">{home}</span>
          </div>
          <div className="bd-kickoff-mid">
            {showScore && (homeGoals != null || awayGoals != null) ? (
              <span className="bd-kickoff-score tabular-nums">
                {homeGoals ?? 0} — {awayGoals ?? 0}
              </span>
            ) : (
              <span className="bd-kickoff-status">{short}</span>
            )}
            <span className="bd-kickoff-when tabular-nums">{kickoffTime}</span>
          </div>
          <div className="bd-kickoff-team bd-kickoff-team-away">
            <span className="truncate">{away}</span>
            <TeamLogo team={awayTeam} size="sm" />
          </div>
        </div>
      </div>
    </section>
  );
};

export const LowLiquidityBanner = () => (
  <div className="bd-low-liq">
    <AlertCircle size={16} />
    <p>
      Withdrawable pool is near this house&apos;s low-balance threshold. Check the Pool line
      in the header before placing large stakes.
    </p>
  </div>
);

/* ── House pulse ── */

export const HousePulseSection = ({ pulse }) => (
  <section className="bd-panel bd-pulse-panel">
    <div className="bd-pulse-head">
      <div className="bd-pulse-head-left">
        <span className="bd-pulse-dot" aria-hidden />
        <div>
          <p className="bd-panel-tag">House pulse</p>
          <p className="bd-pulse-sub">Live snapshot of this board</p>
        </div>
      </div>
      <span className={`bd-pulse-status ${pulse.isPaused ? "bd-pulse-status-paused" : ""}`}>
        {pulse.statusLabel}
      </span>
    </div>

    <div className="bd-pulse-hero">
      <Wallet size={14} />
      <div>
        <p className="bd-pulse-hero-label">Total volume</p>
        <p className="bd-pulse-hero-value tabular-nums">
          {fmtFull(pulse.betVolume)} <span>Cash</span>
        </p>
        <p className="bd-pulse-hero-hint">
          {fmtFull(pulse.totalTickets)} tickets · {fmtFull(pulse.uniqueBettors)} unique bettors
        </p>
      </div>
    </div>

    <div className="bd-pulse-bento">
      <div className="bd-pulse-tile">
        <Ticket size={14} />
        <span className="bd-pulse-tile-value tabular-nums">{fmtFull(pulse.totalTickets)}</span>
        <span className="bd-pulse-tile-label">Tickets placed</span>
      </div>
      <div className="bd-pulse-tile">
        <BarChart2 size={14} />
        <span className="bd-pulse-tile-value tabular-nums">{fmtCash(pulse.avgTicketSize)}</span>
        <span className="bd-pulse-tile-label">Avg ticket size</span>
      </div>
    </div>

    <p className="bd-pulse-reach-title">Audience</p>
    <div className="bd-pulse-reach">
      <div className="bd-pulse-reach-row">
        <span className="bd-pulse-reach-label">
          <Eye size={12} /> Views
        </span>
        <span className="bd-pulse-reach-bar" aria-hidden>
          <span style={{ width: `${pulse.viewsBarPct}%` }} />
        </span>
        <span className="bd-pulse-reach-value tabular-nums">{fmtFull(pulse.viewsCount)}</span>
      </div>
      <div className="bd-pulse-reach-row">
        <span className="bd-pulse-reach-label bd-pulse-reach-label-follow">
          <Heart size={12} /> Followers
        </span>
        <span className="bd-pulse-reach-bar bd-pulse-reach-bar-follow" aria-hidden>
          <span style={{ width: `${pulse.followersBarPct}%` }} />
        </span>
        <span className="bd-pulse-reach-value tabular-nums">{fmtFull(pulse.followersCount)}</span>
      </div>
    </div>
  </section>
);

/* ── Brand notes ── */

export const BrandNotesSection = ({ brandStory }) => {
  const [expanded, setExpanded] = useState(false);
  if (!brandStory) return null;
  const isLong = brandStory.length > 240;

  return (
    <section className="bd-panel">
      <PanelHead
        tag="House"
        title="Brand notes"
        subtitle="From the operators of this bet house"
        Icon={MessageSquare}
      />
      <p className={`bd-brand-body ${!expanded && isLong ? "bd-brand-body-clamp" : ""}`}>
        {brandStory}
      </p>
      {isLong ? (
        <button type="button" className="bd-brand-toggle" onClick={() => setExpanded((v) => !v)}>
          {expanded ? "Show less" : "Read more"}
        </button>
      ) : null}
    </section>
  );
};

/* ── About this house ── */

export const AboutHouseSection = ({ about }) => (
  <section className="bd-panel">
    <PanelHead
      tag="Discover"
      title="About this house"
      subtitle="Access rules, how this board is positioned, and key dates."
    />

    <div className="bd-about-access">
      <span className="bd-about-access-icon">
        {about.visibilityMode === "followers" ? <Users size={14} /> : <Globe size={14} />}
      </span>
      <div>
        <p className="bd-about-access-eyebrow">Access</p>
        <p className="bd-about-access-title">
          {about.visibilityMode === "followers" ? "Followers only" : "Public house"}
        </p>
        <p className="bd-about-access-body">
          {about.visibilityMode === "followers"
            ? "Players may need to follow this house before they can see every line or place bets."
            : "Anyone on BetBond can browse this house and join the board without an invite."}
        </p>
      </div>
    </div>

    {about.tags.length > 0 ? (
      <div className="bd-about-tags">
        <p className="bd-about-tags-title">Categories</p>
        <div className="bd-about-tags-row">
          {about.tags.map((tag) => (
            <span key={tag} className="bd-about-tag">
              {tag}
            </span>
          ))}
        </div>
      </div>
    ) : null}

    {(about.houseSince || about.houseClosedAt) && (
      <div className="bd-about-milestones">
        <p className="bd-about-tags-title">Milestones</p>
        {about.houseSince ? (
          <div className="bd-about-milestone">
            <Calendar size={13} />
            <div>
              <span className="bd-about-milestone-label">Opened</span>
              <span className="bd-about-milestone-value">{about.houseSince}</span>
            </div>
          </div>
        ) : null}
        {about.houseClosedAt ? (
          <div className="bd-about-milestone">
            <Lock size={15} />
            <div>
              <span className="bd-about-milestone-label">Closed</span>
              <span className="bd-about-milestone-value bd-about-milestone-closed">
                {about.houseClosedAt}
              </span>
            </div>
          </div>
        ) : null}
      </div>
    )}

    {about.amountAllocated > 0 ? (
      <div className="bd-about-capital">
        <Briefcase size={14} />
        <div>
          <p className="bd-about-capital-title">Partner capital</p>
          <p className="bd-about-capital-amount tabular-nums">{fmtCash(about.amountAllocated)}</p>
          <p className="bd-about-capital-hint">
            Total backing pledged by operators for this house (transparency).
          </p>
        </div>
      </div>
    ) : null}
  </section>
);

/* ── Stakes & limits — parameter deck ── */

const ParamValue = ({ row }) => {
  if (row.kind === "cash") {
    return (
      <>
        <span className="bd-param-num tabular-nums">{fmtFull(row.amount)}</span>
        <span className="bd-param-unit">Cash</span>
      </>
    );
  }
  return <span className="bd-param-num tabular-nums">{row.value}</span>;
};

export const StakesLimitsSection = ({ rows }) => {
  const minBet = rows.find((r) => r.key === "minBet");
  const maxBet = rows.find((r) => r.key === "maxBet");
  const satellites = rows.filter((r) => r.key !== "minBet" && r.key !== "maxBet");

  const minAmt = minBet?.amount || 0;
  const maxAmt = maxBet?.amount || 0;
  const rangePct =
    maxAmt > minAmt && maxAmt > 0
      ? Math.min(100, Math.max(8, ((maxAmt - minAmt) / maxAmt) * 100))
      : 100;

  return (
    <section className="bd-params">
      <div className="bd-params-head">
        <div className="bd-params-head-copy">
          <p className="bd-params-tag">Parameters</p>
          <h2 className="bd-params-title">Stakes &amp; limits</h2>
          <p className="bd-params-sub">What applies to every ticket on this house</p>
        </div>
        <div className="bd-params-shield" aria-hidden>
          <ShieldCheck size={16} strokeWidth={2.2} />
        </div>
      </div>

      <div className="bd-params-bridge">
        {minBet ? (
          <article className="bd-param-pole bd-param-pole-min">
            <span className="bd-param-pole-label">{minBet.shortLabel}</span>
            <div className="bd-param-pole-value">
              <ParamValue row={minBet} />
            </div>
            {minBet.hint ? <span className="bd-param-pole-hint">{minBet.hint}</span> : null}
          </article>
        ) : null}

        <div className="bd-params-spine">
          <div className="bd-params-spine-track" aria-hidden>
            <span className="bd-params-spine-fill" style={{ width: `${rangePct}%` }} />
          </div>
          <span className="bd-params-spine-label">Stake window</span>
        </div>

        {maxBet ? (
          <article className="bd-param-pole bd-param-pole-max">
            <span className="bd-param-pole-label">{maxBet.shortLabel}</span>
            <div className="bd-param-pole-value">
              <ParamValue row={maxBet} />
            </div>
            {maxBet.hint ? <span className="bd-param-pole-hint">{maxBet.hint}</span> : null}
          </article>
        ) : null}
      </div>

      {satellites.length > 0 ? (
        <div className="bd-params-satellites">
          {satellites.map((row) => (
            <article key={row.key} className="bd-param-sat">
              <span className="bd-param-sat-label">{row.shortLabel || row.label}</span>
              <div className="bd-param-sat-value">
                <ParamValue row={row} />
              </div>
              {row.hint ? <span className="bd-param-sat-hint">{row.hint}</span> : null}
            </article>
          ))}
        </div>
      ) : null}
    </section>
  );
};

/* ── Betting pulse ── */

export const BettingPulseSection = ({ pulse, analytics, payout }) => (
  <section className="bd-panel">
    <PanelHead
      tag="Activity"
      title="Betting pulse"
      subtitle="Live volume and the last stretch of tickets on this house"
      Icon={BarChart2}
    />

    <div className="bd-bet-volume">
      <p className="bd-bet-volume-label">Total volume (all tickets)</p>
      <p className="bd-bet-volume-value tabular-nums">{fmtFull(pulse.betVolume)} Cash</p>
      <p className="bd-bet-volume-sub">
        Based on {analytics ? analytics.count : 0} recorded stakes
      </p>
    </div>

    {analytics ? (
      <>
        {analytics.recentBets.length > 0 && (
          <div className="bd-stake-chart">
            <div className="bd-stake-chart-head">
              <span>Last 10 stakes</span>
              {analytics.lastBetTime ? (
                <span className="bd-stake-chart-time">
                  Last{" "}
                  {formatAppDateTime(analytics.lastBetTime)}
                </span>
              ) : null}
            </div>
            <div className="bd-stake-bars">
              {analytics.recentBets.map((entry) => {
                const maxAmount = Math.max(
                  ...analytics.recentBets.map((b) => b.amount || 0),
                  1
                );
                const heightPct = (entry.amount / maxAmount) * 100;
                return (
                  <div key={entry.key} className="bd-stake-bar-cell">
                    <div className="bd-stake-bar-track">
                      <span
                        className="bd-stake-bar-fill"
                        style={{ height: `${Math.max(heightPct, 8)}%` }}
                      />
                    </div>
                    <span className="bd-stake-bar-label">{entry.label}</span>
                    <span className="bd-stake-bar-amt tabular-nums">{fmtCompact(entry.amount)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="bd-mini-stats">
          <div>
            <span className="bd-mini-stat-label">Average</span>
            <span className="bd-mini-stat-value tabular-nums">{fmtCash(analytics.avg)}</span>
          </div>
          <div>
            <span className="bd-mini-stat-label">Highest</span>
            <span className="bd-mini-stat-value tabular-nums">{fmtCash(analytics.highest)}</span>
          </div>
          <div>
            <span className="bd-mini-stat-label">Lowest</span>
            <span className="bd-mini-stat-value tabular-nums">{fmtCash(analytics.lowest)}</span>
          </div>
          <div>
            <span className="bd-mini-stat-label">Range</span>
            <span className="bd-mini-stat-value tabular-nums">
              {fmtFull(analytics.highest - analytics.lowest)}
            </span>
          </div>
        </div>
      </>
    ) : (
      <p className="bd-empty-copy">
        No bets yet. When players start staking, volume trends and stake sizes will appear here.
      </p>
    )}

    {payout.hasData ? (
      <div className="bd-payout">
        <div className="bd-payout-head">
          <span className="bd-payout-title">Payout mix</span>
          <span
            className={`bd-payout-profit tabular-nums ${
              payout.houseProfit >= 0 ? "bd-payout-profit-pos" : "bd-payout-profit-neg"
            }`}
          >
            {payout.houseProfit >= 0 ? "+" : ""}
            {fmtFull(payout.houseProfit)} Cash
          </span>
        </div>
        <div className="bd-payout-bar" aria-hidden>
          <span style={{ width: `${payout.winPct}%` }} className="bd-payout-bar-win" />
          <span style={{ width: `${payout.lossPct}%` }} className="bd-payout-bar-loss" />
        </div>
        <div className="bd-payout-legend">
          <span className="bd-payout-won">Won {fmtFull(payout.winAmount)} Cash</span>
          <span className="bd-payout-lost">Lost {fmtFull(payout.lossAmount)} Cash</span>
        </div>
      </div>
    ) : null}
  </section>
);

/* ── Fixtures schedule ── */

export const FixturesScheduleSection = ({ fixtures, isPaused }) => (
  <section className="bd-panel">
    <PanelHead
      tag="Schedule"
      title="Fixtures"
      subtitle={`${fixtures.length} match${fixtures.length === 1 ? "" : "es"} on this board`}
      Icon={BarChart2}
    />

    {fixtures.length === 0 ? (
      <div className="bd-fixtures-empty">
        <span className={`bd-fixtures-empty-icon ${isPaused ? "bd-fixtures-empty-paused" : ""}`}>
          {isPaused ? <PauseCircle size={32} /> : <Calendar size={32} />}
        </span>
        <p className="bd-fixtures-empty-title">
          {isPaused ? "House is paused" : "No fixtures on this house"}
        </p>
        <p className="bd-empty-copy">
          {isPaused
            ? "Betting is on hold. Fixtures will appear again when the owner resumes the house."
            : "There are no matches listed here yet. When the owner adds the next round, fixtures will show in this section."}
        </p>
      </div>
    ) : (
      <ul className="bd-fixture-list">
        {fixtures.map((fixture) => {
          const { showScore, home, away, short } = getFixtureScore(fixture);
          const suspended = !!fixture.suspended;
          const marketCount = getFixtureMarketCount(fixture);
          return (
            <li
              key={fixture._id}
              className={`bd-fixture-card ${suspended ? "bd-fixture-card-suspended" : ""}`}
            >
              {fixture.league?.name ? (
                <div className="bd-fixture-league">
                  {fixture.league.logo ? (
                    // eslint-disable-next-line jsx-a11y/alt-text
                    <img src={fixture.league.logo} loading="lazy" />
                  ) : null}
                  <span className="truncate">{fixture.league.name}</span>
                </div>
              ) : null}
              <div className="bd-fixture-main">
                <div className="bd-fixture-team">
                  <TeamLogo team={fixture.teams?.home} size="sm" />
                  <span className="truncate">{fixture.teams?.home?.name || "Home"}</span>
                </div>
                <div className="bd-fixture-mid">
                  {showScore && (home != null || away != null) ? (
                    <span className="bd-fixture-score tabular-nums">
                      {home ?? 0} — {away ?? 0}
                    </span>
                  ) : (
                    <span className="bd-fixture-status">{short}</span>
                  )}
                </div>
                <div className="bd-fixture-team bd-fixture-team-away">
                  <span className="truncate">{fixture.teams?.away?.name || "Away"}</span>
                  <TeamLogo team={fixture.teams?.away} size="sm" />
                </div>
              </div>
              <div className="bd-fixture-foot">
                <span className="bd-fixture-foot-markets">
                  {marketCount} market{marketCount === 1 ? "" : "s"}
                </span>
                <span>{fixture.bettorsCount || 0} bettors</span>
                {suspended ? <span className="bd-fixture-foot-paused">Paused</span> : null}
              </div>
            </li>
          );
        })}
      </ul>
    )}
  </section>
);
